/**
 * Central object used for Store App module
 */
// import { useI18n } from 'vue-i18n'
import type ISnackbar from '@/interfaces/internal-resources/Snackbar'
import type { IInterviewType } from '@/models/Interview/InterviewSessionType'
import type { RouteRecordName } from 'vue-router'
import type { TutorialVideos } from '@/interfaces/internal-resources/TutorialVideo'

interface AppState {
  config: {
    APP_ENV: string | null
    APP_NAME: string | null
    APP_URL: string | null
    ASSET_URL: string | null
    AUTH_PUBLIC_CLIENT_ID: string | null
    AUTH_BASE_URL: string | null
    DARA_BASE_URL: string | null
    DCD_BASE_URL: string | null
    DEV_BAR : boolean | null
    MYCC_BASE_URL: string | null
    MYCS_BASE_URL: string | null
    MYFJ_BASE_URL: string | null
    MYHQ_BASE_URL: string | null
    MYHQ_V2_BASE_URL: string | null
    MYPP_BASE_URL: string | null
    MYA_BASE_URL: string | null
    MY1C_BASE_URL: string | null
  }
  settings: {
    INTERVIEW_TYPES: IInterviewType[]
    MFC_STATUS_PAGE_URL: string
    TUTORIAL_VIDEOS: TutorialVideos
  },
  error: string | null
  academicYearWelcomeModal: {
    active: boolean
    shouldDisplay: boolean
  }
  leaveWarningModal: {
    active: boolean
    confirmed: boolean
    intendedRoute: RouteRecordName | undefined
    text: string | null
    title: string | null
  }
  locale: string | null
  showPreviewBarModal: boolean
  showQuickHelpDrawer: boolean
  showMyHQWelcomeModal: boolean
  snackbar: ISnackbar
  $i18n: object
  theme: string | null
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    config: {
      APP_ENV: null,
      APP_NAME: null,
      APP_URL: null,
      ASSET_URL: null,
      AUTH_PUBLIC_CLIENT_ID: null,
      AUTH_BASE_URL: null,
      DARA_BASE_URL: null,
      DCD_BASE_URL: null,
      DEV_BAR: null,
      MYCC_BASE_URL: null,
      MYCS_BASE_URL: null,
      MYFJ_BASE_URL: null,
      MYHQ_BASE_URL: null,
      MYHQ_V2_BASE_URL: null,
      MYPP_BASE_URL: null,
      MYA_BASE_URL: null,
      MY1C_BASE_URL: null,
    },
    settings: {
      INTERVIEW_TYPES: [],
      MFC_STATUS_PAGE_URL: '',
      TUTORIAL_VIDEOS: {},
    },
    error: null,
    academicYearWelcomeModal: {
      active: false,
      shouldDisplay: false,
    },
    leaveWarningModal: {
      active: false,
      confirmed: false,
      intendedRoute: undefined,
      text: null,
      title: null,
    },
    locale: null,
    showPreviewBarModal: false,
    showQuickHelpDrawer: false,
    showMyHQWelcomeModal: false,
    snackbar: {
      colour: 'primary',
      multiLine: false,
      text: '',
      timeout: 8000,
      variant: 'elevated',
    },
    $i18n: {},
    theme: null,
  }),

  getters: {
    daraApiBaseUrl: state => state.config.DARA_BASE_URL + '/api/myhq-v3',
  },

  actions: {
    resetLeaveWarningModal() {
      this.leaveWarningModal = {
        active: false,
        confirmed: false,
        intendedRoute: undefined,
        text: null,
        title: null,
      }
    },
    setError(error: string) {
      this.error = error
    },
    setSnackbar(snackbar: ISnackbar) {
      // const i18n = useI18n()

      // if (isI18nMessage)
      //   snackbar.text = i18n.t(snackbar.text)

      this.snackbar = {
        colour: snackbar.colour ?? 'primary',
        multiLine: snackbar.multiLine ?? false,
        text: snackbar.text ?? '',
        timeout: snackbar.timeout ?? 8000,
        variant: snackbar.variant ?? 'elevated',
      }
    },
    setTheme(theme: string) {
      this.theme = theme
    },
    setDocumentTitle(title: string) {
      document.title = `${this.config.APP_NAME} - ${title}`
    },
  },
})
